import { useState, useEffect } from "react";
import { supabase } from "../../integrations/supabase/client";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Upload, Calendar, FolderPlus, ExternalLink, Folder, Pencil } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

interface GalleryPhoto {
  id: string;
  title: string;
  description: string | null;
  image_url: string;
  event_name: string;
  event_date: string | null;
  created_at: string;
  is_public: boolean;
}

interface GalleryAlbum {
  id: string;
  event_name: string;
  event_date: string | null;
  description: string | null;
  external_link: string | null;
  cover_image_url: string | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export const GalleryManager = () => {
  const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
  const [albums, setAlbums] = useState<GalleryAlbum[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAlbumDialogOpen, setIsAlbumDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAlbumDialogOpen, setDeleteAlbumDialogOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<GalleryPhoto | null>(null);
  const [albumToDelete, setAlbumToDelete] = useState<GalleryAlbum | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState<GalleryAlbum | null>(null);
  const [albumPhotos, setAlbumPhotos] = useState<GalleryPhoto[]>([]);
  const [isAlbumViewOpen, setIsAlbumViewOpen] = useState(false);
  const [isPhotoUploadOpen, setIsPhotoUploadOpen] = useState(false);
  const [editingAlbumId, setEditingAlbumId] = useState<string | null>(null);
  const [albumEditData, setAlbumEditData] = useState({
    event_name: "",
    event_date: "",
    description: "",
    external_link: "",
  });
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_name: "",
    event_date: "",
    image_files: null as FileList | null,
  });

  const [albumFormData, setAlbumFormData] = useState({
    event_name: "",
    event_date: "",
    description: "",
    external_link: "",
    photo_files: null as FileList | null,
    photo_title: "",
    photo_description: "",
  });

  useEffect(() => {
    fetchPhotos();
    fetchAlbums();
  }, []);

  const fetchPhotos = async () => {
    try {
      const { data, error } = await supabase
        .from("gallery_photos")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPhotos(data || []);
    } catch (error) {
      console.error("Error fetching photos:", error);
      toast({
        title: "Error",
        description: "Failed to fetch photos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAlbums = async () => {
    try {
      const { data, error } = await supabase
        .from("gallery_albums")
        .select("*")
        .order("event_date", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      setAlbums(data || []);
    } catch (error) {
      console.error("Error fetching albums:", error);
      toast({
        title: "Error",
        description: "Failed to fetch albums",
        variant: "destructive",
      });
    }
  };

  const fetchAlbumPhotos = async (albumId: string) => {
    const album = albums.find(a => a.id === albumId);
    if (!album) return;
    
    try {
      const { data, error } = await supabase
        .from("gallery_photos")
        .select("*")
        .eq("event_name", album.event_name)
        .order("created_at", { ascending: false });
        
      if (error) throw error;
      setAlbumPhotos(data || []);
    } catch (error) {
      console.error("Error fetching album photos:", error);
      toast({
        title: "Error",
        description: "Failed to fetch album photos",
        variant: "destructive",
      });
    }
  };

  const handleAlbumClick = (album: GalleryAlbum) => {
    setSelectedAlbum(album);
    fetchAlbumPhotos(album.id);
    setIsAlbumViewOpen(true);
  };

  const handleDeletePhotoFromAlbum = async (photo: GalleryPhoto) => {
    setPhotoToDelete(photo);
    setDeleteDialogOpen(true);
  };

  const handleUploadToAlbum = () => {
    if (selectedAlbum) {
      setFormData({
        title: "", // Will be auto-generated
        description: "",
        event_name: selectedAlbum.event_name,
        event_date: selectedAlbum.event_date || "",
        image_files: null,
      });
      setIsPhotoUploadOpen(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For album uploads, we don't require title (will auto-generate)
    const isAlbumUpload = isPhotoUploadOpen && selectedAlbum;
    
    if (!isAlbumUpload && !formData.title.trim()) {
      toast({
        title: "Validation Error",
        description: "Title is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.event_name.trim() || !formData.image_files || formData.image_files.length === 0) {
      toast({
        title: "Validation Error",
        description: "Event name and at least one image are required",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploading(true);
      const filesArray = Array.from(formData.image_files);
      let successCount = 0;
      let errorCount = 0;

      // Upload each file
      for (const file of filesArray) {
        try {
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
          const filePath = `gallery/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from("general-media")
            .upload(filePath, file);

          if (uploadError) throw uploadError;

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from("general-media")
            .getPublicUrl(filePath);

          // Insert photo record
          // Auto-generate title for album uploads
          const photoTitle = isAlbumUpload 
            ? `Photo ${successCount + 1}` 
            : `${formData.title}${filesArray.length > 1 ? ` (${successCount + 1})` : ''}`;

          const { error: insertError } = await supabase.from("gallery_photos").insert({
            title: photoTitle,
            description: formData.description || null,
            image_url: publicUrl,
            event_name: formData.event_name,
            event_date: formData.event_date || null,
            is_public: true,
          });

          if (insertError) throw insertError;
          successCount++;
        } catch (error) {
          console.error("Error uploading file:", error);
          errorCount++;
        }
      }

      toast({
        title: "Upload Complete",
        description: `${successCount} photo(s) uploaded successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
        variant: errorCount > 0 ? "destructive" : "default",
      });

      resetForm();
      setIsPhotoUploadOpen(false);
      fetchPhotos();
      if (selectedAlbum) {
        fetchAlbumPhotos(selectedAlbum.id);
      }
    } catch (error) {
      console.error("Error uploading photos:", error);
      toast({
        title: "Error",
        description: "Failed to upload photos",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!photoToDelete) return;

    try {
      // Extract file path from URL
      const urlParts = photoToDelete.image_url.split('/');
      const filePath = `gallery/${urlParts[urlParts.length - 1]}`;

      // Delete from storage
      await supabase.storage.from("general-media").remove([filePath]);

      // Delete from database
      const { error } = await supabase
        .from("gallery_photos")
        .delete()
        .eq("id", photoToDelete.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Photo deleted successfully",
      });

      fetchPhotos();
      if (selectedAlbum) {
        fetchAlbumPhotos(selectedAlbum.id);
      }
    } catch (error) {
      console.error("Error deleting photo:", error);
      toast({
        title: "Error",
        description: "Failed to delete photo",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setPhotoToDelete(null);
    }
  };

  const handleAlbumSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!albumFormData.event_name.trim()) {
      toast({
        title: "Validation Error",
        description: "Event name is required",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploading(true);

      // Insert album record
      const { data: albumData, error: insertError } = await supabase
        .from("gallery_albums")
        .insert({
          event_name: albumFormData.event_name,
          event_date: albumFormData.event_date || null,
          description: albumFormData.description || null,
          external_link: albumFormData.external_link || null,
          cover_image_url: null,
          is_public: true,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Upload photos if provided
      if (albumFormData.photo_files && albumFormData.photo_files.length > 0) {
        const photoUploadPromises = Array.from(albumFormData.photo_files).map(async (file, index) => {
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
          const filePath = `gallery/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('general-media')
            .upload(filePath, file);

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('general-media')
            .getPublicUrl(filePath);

          return supabase.from("gallery_photos").insert({
            event_name: albumFormData.event_name,
            event_date: albumFormData.event_date || null,
            title: albumFormData.photo_title ? `${albumFormData.photo_title} ${index + 1}` : `Photo ${index + 1}`,
            description: albumFormData.photo_description || null,
            image_url: publicUrl,
            is_public: true,
          });
        });

        await Promise.all(photoUploadPromises);
        const photoCount = albumFormData.photo_files.length;
        toast({ 
          title: "Success", 
          description: `Album created with ${photoCount} photo(s)` 
        });
      } else {
        toast({ 
          title: "Success", 
          description: "Album created successfully" 
        });
      }

      resetAlbumForm();
      fetchAlbums();
      fetchPhotos();
    } catch (error) {
      console.error("Error creating album:", error);
      toast({
        title: "Error",
        description: "Failed to create album",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAlbum = async () => {
    if (!albumToDelete) return;

    try {
      // Delete cover image from storage if exists
      if (albumToDelete.cover_image_url) {
        const urlParts = albumToDelete.cover_image_url.split('/');
        const filePath = `album-covers/${urlParts[urlParts.length - 1]}`;
        await supabase.storage.from("general-media").remove([filePath]);
      }

      // Delete from database
      const { error } = await supabase
        .from("gallery_albums")
        .delete()
        .eq("id", albumToDelete.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Album deleted successfully",
      });

      // Close album view if the deleted album is currently open
      setIsAlbumViewOpen(false);
      setSelectedAlbum(null);
      
      fetchAlbums();
    } catch (error) {
      console.error("Error deleting album:", error);
      toast({
        title: "Error",
        description: "Failed to delete album",
        variant: "destructive",
      });
    } finally {
      setDeleteAlbumDialogOpen(false);
      setAlbumToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      event_name: "",
      event_date: "",
      image_files: null,
    });
    setIsDialogOpen(false);
  };

  const resetAlbumForm = () => {
    setAlbumFormData({
      event_name: "",
      event_date: "",
      description: "",
      external_link: "",
      photo_files: null,
      photo_title: "",
      photo_description: "",
    });
    setIsAlbumDialogOpen(false);
    setIsPhotoUploadOpen(false);
  };

  const handleEditAlbum = (album: GalleryAlbum) => {
    setEditingAlbumId(album.id);
    setAlbumEditData({
      event_name: album.event_name,
      event_date: album.event_date || "",
      description: album.description || "",
      external_link: album.external_link || "",
    });
  };

  const handleUpdateAlbum = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAlbum || !editingAlbumId) return;

    if (!albumEditData.event_name.trim()) {
      toast({
        title: "Validation Error",
        description: "Event name is required",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploading(true);

      const { error } = await supabase
        .from("gallery_albums")
        .update({
          event_name: albumEditData.event_name,
          event_date: albumEditData.event_date || null,
          description: albumEditData.description || null,
          external_link: albumEditData.external_link || null,
        })
        .eq("id", editingAlbumId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Album updated successfully",
      });

      setEditingAlbumId(null);
      setSelectedAlbum({
        ...selectedAlbum,
        event_name: albumEditData.event_name,
        event_date: albumEditData.event_date || null,
        description: albumEditData.description || null,
        external_link: albumEditData.external_link || null,
      });
      fetchAlbums();
    } catch (error) {
      console.error("Error updating album:", error);
      toast({
        title: "Error",
        description: "Failed to update album",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const cancelEditAlbum = () => {
    setEditingAlbumId(null);
    setAlbumEditData({
      event_name: "",
      event_date: "",
      description: "",
      external_link: "",
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading photos...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Albums Section - No Tabs */}
      <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Gallery Albums</h2>
            <Dialog open={isAlbumDialogOpen} onOpenChange={setIsAlbumDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setIsAlbumDialogOpen(true)}>
                  <FolderPlus className="h-4 w-4 mr-2" />
                  Create Album
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create Gallery Album</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAlbumSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="album_event_name">Event Name *</Label>
                    <Input
                      id="album_event_name"
                      value={albumFormData.event_name}
                      onChange={(e) => setAlbumFormData({ ...albumFormData, event_name: e.target.value })}
                      required
                      placeholder="e.g., National Conference 2024"
                    />
                  </div>

                  <div>
                    <Label htmlFor="album_event_date">Event Date</Label>
                    <Input
                      id="album_event_date"
                      type="date"
                      value={albumFormData.event_date}
                      onChange={(e) => setAlbumFormData({ ...albumFormData, event_date: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="album_description">Description</Label>
                    <Textarea
                      id="album_description"
                      value={albumFormData.description}
                      onChange={(e) => setAlbumFormData({ ...albumFormData, description: e.target.value })}
                      rows={3}
                      placeholder="Brief description of the album"
                    />
                  </div>

                  <div>
                    <Label htmlFor="album_external_link">External Gallery Link</Label>
                    <Input
                      id="album_external_link"
                      type="url"
                      value={albumFormData.external_link}
                      onChange={(e) => setAlbumFormData({ ...albumFormData, external_link: e.target.value })}
                      placeholder="https://photos.google.com/..."
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Link to Google Photos, Dropbox, or other external gallery
                    </p>
                  </div>

                <div>
                  <Label htmlFor="album_photos">Upload Photos (Optional, Multiple files supported)</Label>
                  <Input
                    id="album_photos"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => setAlbumFormData({ ...albumFormData, photo_files: e.target.files })}
                  />
                  {albumFormData.photo_files && albumFormData.photo_files.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {albumFormData.photo_files.length} file(s) selected
                    </p>
                  )}
                </div>

                {albumFormData.photo_files && albumFormData.photo_files.length > 0 && (
                  <>
                    <div>
                      <Label htmlFor="photo_title">Photo Title (for all photos)</Label>
                      <Input
                        id="photo_title"
                        value={albumFormData.photo_title}
                        onChange={(e) => setAlbumFormData({ ...albumFormData, photo_title: e.target.value })}
                        placeholder="e.g., Conference Day 1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="photo_description">Photo Description (Optional)</Label>
                      <Textarea
                        id="photo_description"
                        value={albumFormData.photo_description}
                        onChange={(e) => setAlbumFormData({ ...albumFormData, photo_description: e.target.value })}
                        rows={2}
                      />
                    </div>
                  </>
                )}

                  <div className="flex gap-2">
                    <Button type="submit" disabled={uploading}>
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? "Creating..." : "Create Album"}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetAlbumForm}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {albums.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No albums yet. Create your first album!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {albums.map((album) => (
                <div 
                  key={album.id}
                  className="flex flex-col items-center group cursor-pointer"
                  onClick={() => handleAlbumClick(album)}
                >
                  <div className="relative">
                    <Folder 
                      className="h-24 w-24 text-primary group-hover:text-primary/80 transition-colors" 
                      fill="currentColor"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div className="text-center mt-2 space-y-1">
                    <p className="font-medium text-sm line-clamp-2 px-2">{album.event_name}</p>
                    {album.event_date && (
                      <p className="text-xs text-muted-foreground">
                        {new Date(album.event_date).toLocaleDateString()}
                      </p>
                    )}
                    {album.external_link && (
                      <Button
                        size="sm"
                        variant="link"
                        className="h-auto p-0 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(album.external_link, '_blank');
                        }}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        External
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      {/* Delete Photo Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{photoToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {photoToDelete?.image_url && (
            <div className="my-4 flex justify-center">
              <img 
                src={photoToDelete.image_url} 
                alt={photoToDelete.title}
                className="max-w-full max-h-64 object-contain rounded-lg border"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Album Dialog */}
      <AlertDialog open={deleteAlbumDialogOpen} onOpenChange={setDeleteAlbumDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the album "{albumToDelete?.event_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAlbum}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Album Detail View Dialog */}
      <Dialog open={isAlbumViewOpen} onOpenChange={setIsAlbumViewOpen}>
        <DialogContent className="w-screen h-screen max-w-[100vw] max-h-[100vh] overflow-y-auto p-6">
          <DialogHeader className="pr-8">
            <div className="flex items-start justify-between gap-4">
              <DialogTitle className="flex items-center gap-2 flex-1">
                <Folder className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="break-words">{selectedAlbum?.event_name}</span>
                {selectedAlbum?.external_link && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(selectedAlbum.external_link, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
              </DialogTitle>
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => selectedAlbum && handleEditAlbum(selectedAlbum)}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Album
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setAlbumToDelete(selectedAlbum);
                    setDeleteAlbumDialogOpen(true);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Album
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {editingAlbumId === selectedAlbum?.id ? (
              <form onSubmit={handleUpdateAlbum} className="space-y-4 bg-muted/30 p-4 rounded-lg">
                <div>
                  <Label htmlFor="edit_event_name">Event Name *</Label>
                  <Input
                    id="edit_event_name"
                    value={albumEditData.event_name}
                    onChange={(e) => setAlbumEditData({ ...albumEditData, event_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_event_date">Event Date</Label>
                  <Input
                    id="edit_event_date"
                    type="date"
                    value={albumEditData.event_date}
                    onChange={(e) => setAlbumEditData({ ...albumEditData, event_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_description">Description</Label>
                  <Textarea
                    id="edit_description"
                    value={albumEditData.description}
                    onChange={(e) => setAlbumEditData({ ...albumEditData, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_external_link">External Gallery Link</Label>
                  <Input
                    id="edit_external_link"
                    type="url"
                    value={albumEditData.external_link}
                    onChange={(e) => setAlbumEditData({ ...albumEditData, external_link: e.target.value })}
                    placeholder="https://photos.google.com/..."
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={uploading}>
                    {uploading ? "Saving..." : "Save Changes"}
                  </Button>
                  <Button type="button" variant="outline" onClick={cancelEditAlbum}>
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <>
                {selectedAlbum?.event_date && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    {new Date(selectedAlbum.event_date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                )}

                {selectedAlbum?.description && selectedAlbum.description.trim() && (
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="text-sm">{selectedAlbum.description}</p>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Event Photo Gallery</h3>
              <Dialog open={isPhotoUploadOpen} onOpenChange={setIsPhotoUploadOpen}>
                <DialogTrigger asChild>
                  <Button onClick={handleUploadToAlbum}>
                    <Plus className="h-4 w-4 mr-2" />
                    Upload More Photos
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Upload Photos to {selectedAlbum?.event_name}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="image_files">Select Images *</Label>
                      <Input
                        id="image_files"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => setFormData({ ...formData, image_files: e.target.files })}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Photos will be automatically numbered (Photo 1, Photo 2, etc.)
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={uploading}>
                        <Upload className="h-4 w-4 mr-2" />
                        {uploading ? "Uploading..." : "Upload"}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setIsPhotoUploadOpen(false);
                          resetForm();
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {albumPhotos.length === 0 ? (
              <div className="text-center py-12 border rounded-lg bg-muted/20">
                <p className="text-muted-foreground">No photos in this album yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {albumPhotos.map((photo) => (
                  <div key={photo.id} className="relative group">
                    <img
                      src={photo.image_url}
                      alt={photo.title}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeletePhotoFromAlbum(photo)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
